<template>
    <!-- WHAT THE SHEET LOOKS LIKE, beside the thing it changes.
      --
      -- Not in OptionsPanel with the format and the quality, for the reason
      -- SvgBackground.vue records: you judge this by LOOKING, so the controls
      -- belong next to the picture. Here that picture is the preview, which
      -- is why these moved out of the results row when the preview arrived.
      --
      -- FOUR, and the first version had two. The two it had were the two that
      -- matter least: how many across only applies to a grid, and the sheet
      -- now defaults to a collage. What was missing was the choice between
      -- the layouts at all -- reported as "I want something like this" with a
      -- photo collage attached, which the square-cells-only version could not
      -- produce however its other settings were set. -->
    <div id="sheet-control" class="flex flex-col gap-4">

        <div class="flex flex-col gap-1.5">
            <span class="text-[15px] font-bold">{{ t('sheet.layout') }}</span>
            <!-- Buttons rather than a menu: three options whose whole point is
              -- how they LOOK, next to a preview that shows it. A menu hides
              -- two of the three behind a click. -->
            <div class="flex flex-wrap gap-2" role="radiogroup" :aria-label="t('sheet.layout')">
                <button v-for="m in MODES" :key="m" type="button"
                        :data-sheet-mode="m" role="radio"
                        :aria-checked="uiStore.sheetMode === m"
                        class="btn btn-sm rounded-lg"
                        :class="uiStore.sheetMode === m ? 'btn-primary' : 'btn-outline'"
                        @click="uiStore.sheetMode = m">
                    {{ t('sheet.modes.' + m) }}
                </button>
            </div>
            <span class="text-[13px] text-ink-mute leading-snug">
                {{ t('sheet.modeNote.' + uiStore.sheetMode) }}
            </span>
        </div>

        <!-- ONLY FOR A GRID. A collage decides its own rows from the shapes
          -- it is given -- that is what lets every row reach both edges -- so
          -- a column count there would be a control that does nothing, which
          -- is worse than no control.
          --
          -- BUTTONS, NOT A MENU, and the heading carries the unit.
          --
          -- It was a <select> of bare numbers under the word "Across", and
          -- both halves were reported: "what the numbers mean is not
          -- immediately obvious", and the list opens downwards over the two
          -- controls below it so you cannot see what changed. A menu is the
          -- wrong shape for a setting whose whole point is the effect -- it
          -- costs two clicks, hides the alternatives, and covers the page
          -- while it is open. The other two settings here are already button
          -- rows; this one being a menu was the odd one out.
          --
          -- "Pictures in each row" rather than "Across", so a 3 says what it
          -- is three of without anybody having to try it. -->
        <div v-if="uiStore.sheetMode !== 'collage'" class="flex flex-col gap-1.5">
            <span class="text-[15px] font-bold">{{ t('sheet.columns') }}</span>
            <div class="flex flex-wrap gap-2" role="radiogroup" :aria-label="t('sheet.columns')">
                <!-- 0 first, because it is right for almost everybody:
                  -- ceil(sqrt(n)) is what a contact sheet has always done and
                  -- somebody who has not thought about it should not have to.
                  -- It names the number it would pick, so overriding it is a
                  -- decision rather than a guess. -->
                <button type="button" data-sheet-columns="0" role="radio"
                        :aria-checked="uiStore.sheetColumns === 0"
                        class="btn btn-sm rounded-lg"
                        :class="uiStore.sheetColumns === 0 ? 'btn-primary' : 'btn-outline'"
                        @click="uiStore.sheetColumns = 0">
                    {{ t('sheet.auto', { n: auto }) }}
                </button>
                <button v-for="n in choices" :key="n" type="button"
                        :data-sheet-columns="n" role="radio"
                        :aria-checked="uiStore.sheetColumns === n"
                        class="btn btn-sm rounded-lg w-10 px-0"
                        :class="uiStore.sheetColumns === n ? 'btn-primary' : 'btn-outline'"
                        @click="uiStore.sheetColumns = n">
                    {{ n }}
                </button>
            </div>
        </div>

        <div class="flex flex-col gap-1.5">
            <span class="text-[15px] font-bold">{{ t('sheet.spacing') }}</span>
            <div class="flex flex-wrap gap-2" role="radiogroup" :aria-label="t('sheet.spacing')">
                <button v-for="g in GAPS" :key="g" type="button"
                        :data-sheet-gap="g" role="radio"
                        :aria-checked="uiStore.sheetGap === g"
                        class="btn btn-sm rounded-lg"
                        :class="uiStore.sheetGap === g ? 'btn-primary' : 'btn-outline'"
                        @click="uiStore.sheetGap = g">
                    {{ t('sheet.gaps.' + g) }}
                </button>
            </div>
        </div>

        <!-- WHAT IT IS PAINTED ON.
          -- Swatches rather than a free colour input: five is enough for the
          -- job, every one has a name already translated in colours.*, and
          -- each is checked against the luma threshold that decides whether
          -- the filenames come out dark or light. A free picker would let
          -- somebody land on a ground where the labels are invisible.
          --
          -- No transparent swatch, unlike the SVG picker: the sheet is handed
          -- over as a JPEG, which has no alpha, so it would be a control that
          -- lies. -->
        <div class="flex flex-col gap-1.5">
            <span class="text-[15px] font-bold">{{ t('sheet.background') }}</span>
            <div class="flex flex-wrap items-center gap-2"
                 role="radiogroup" :aria-label="t('sheet.background')">
                <button v-for="c in GROUNDS" :key="c.key" type="button"
                        data-sheet-ground :data-sheet-ground-key="c.key" role="radio"
                        :aria-checked="current === c.hex ? 'true' : 'false'"
                        :title="t('sheet.backgroundPick', { name: t('colours.' + c.key) })"
                        :aria-label="t('sheet.backgroundPick', { name: t('colours.' + c.key) })"
                        @click.prevent="uiStore.sheetGround = c.hex"
                        class="h-7 w-7 rounded-full border-2 transition"
                        :class="current === c.hex ? 'border-brand scale-110' : 'border-rule'"
                        :style="{backgroundColor: c.hex}"></button>
            </div>
        </div>

        <!-- OFF TO BEGIN WITH, and stated positively anyway.
          --
          -- Asked for as a "remove filenames" box, and left as "show" for one
          -- reason: a negative checkbox that starts TICKED means the reader
          -- has to untick "remove" to make something appear, which is a double
          -- negative at the exact moment they are deciding. The default is the
          -- half that was actually wrong, and that is flipped -- the label
          -- stays the way round that reads.
          --
          -- One line in SheetControl.vue and one in the store if the other
          -- wording is wanted after all. -->
        <label class="flex items-start gap-2.5 cursor-pointer">
            <input type="checkbox" data-sheet-labels
                   v-model="uiStore.sheetLabels"
                   class="checkbox checkbox-sm checkbox-primary mt-[2px]" />
            <span class="flex flex-col gap-0.5">
                <span class="text-[15px] font-bold">{{ t('sheet.labels') }}</span>
                <span class="text-[13px] text-ink-mute leading-snug">{{ t('sheet.labelsNote') }}</span>
            </span>
        </label>

        <!-- Only once a drag has actually happened. An undo for something
          -- nobody has done is furniture, and it is the same reasoning that
          -- keeps the caption rectangles off a picture with no words on it.
          --
          -- ONE button for both, because they are one gesture: dragging a
          -- picture either moves it or recentres it depending on where it
          -- lands, so "put it back" has to undo whichever happened. Two
          -- buttons would make the reader work out which of them they did. -->
        <button v-if="arranged" type="button" data-sheet-reset-order
                class="btn btn-outline btn-sm rounded-lg self-start"
                @click="uiStore.sheetOrder = []; uiStore.sheetFocus = {}">
            {{ t('sheet.resetOrder') }}
        </button>
    </div>
</template>

<script setup>
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import { useUiElements } from "./../store/uiElements"
import { sheetColumns, SHEET_MODES, SHEET_GAPS, MAX_SHEET_COLUMNS,
         SHEET_GROUNDS, DEFAULT_SHEET_GROUND }
    from "./../lib/contactSheet"

const props = defineProps({
    // How many pictures the sheet will hold. Bounds the column choices:
    // offering "6 across" for three pictures is offering three empty cells.
    count: {type: Number, default: 0}
})

const { t } = useI18n()
const uiStore = useUiElements()

const MODES = SHEET_MODES
const GAPS = Object.keys(SHEET_GAPS)
const GROUNDS = SHEET_GROUNDS

// Empty in the store means the default, so the white swatch reads as chosen on
// a first visit rather than nothing being selected at all.
const current = computed(() => uiStore.sheetGround || DEFAULT_SHEET_GROUND)

// What "work it out" will actually pick, said in the option rather than left
// as a word: somebody deciding whether to override it needs to know what they
// are overriding.
const auto = computed(() => sheetColumns(props.count, 0))

// Has anything been dragged at all -- moved, or slid inside its square.
const arranged = computed(() =>
    uiStore.sheetOrder.length > 0 || Object.keys(uiStore.sheetFocus).length > 0)

const choices = computed(() => {
    const max = Math.max(1, Math.min(MAX_SHEET_COLUMNS, props.count || MAX_SHEET_COLUMNS))
    return Array.from({length: max}, (_, i) => i + 1)
})
</script>
